import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { submitComplaint } from "@/lib/api";
import { Field, Consent, SuccessBox, useFormHelpers } from "./shared";

const init = { name: "", email: "", reference: "", subject: "", message: "", consent: false };

export function ComplaintForm() {
  const { t } = useLang();
  const s = t.contact.complaint;
  const { c, lang, isEmail, errorMessage } = useFormHelpers();
  const [f, setF] = useState(init);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (f.name.trim().length < 2) e.name = c.required;
    if (!isEmail(f.email)) e.email = c.invalidEmail;
    if (f.subject.trim().length < 3) e.subject = c.required;
    if (f.message.trim().length < 10) e.message = c.required;
    if (!f.consent) e.consent = c.consentRequired;
    setErr(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const res = await submitComplaint({ ...f, lang });
      setDone(res.reference);
      toast.success(c.success);
    } catch (er) { toast.error(errorMessage(er)); } finally { setBusy(false); }
  };

  if (done) return <SuccessBox reference={done} id="complaint-form" onReset={() => { setDone(null); setF(init); }} />;

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="complaint-form">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900">{s.title}</h2>
        <p className="mt-1 text-sm text-slate-600">{s.sub}</p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={s.name} error={err.name} id="complaint-name"><input id="complaint-name" className="field" value={f.name} onChange={set("name")} data-testid="complaint-name-input" /></Field>
        <Field label={s.email} error={err.email} id="complaint-email"><input id="complaint-email" type="email" className="field" value={f.email} onChange={set("email")} data-testid="complaint-email-input" /></Field>
        <Field label={s.reference} id="complaint-reference"><input id="complaint-reference" className="field" value={f.reference} onChange={set("reference")} data-testid="complaint-reference-input" /></Field>
        <Field label={s.subject} error={err.subject} id="complaint-subject"><input id="complaint-subject" className="field" value={f.subject} onChange={set("subject")} data-testid="complaint-subject-input" /></Field>
      </div>
      <Field label={s.message} error={err.message} id="complaint-message"><textarea id="complaint-message" rows={5} className="field" value={f.message} onChange={set("message")} data-testid="complaint-message-textarea" /></Field>
      <Consent checked={f.consent} onChange={(v) => setF((x) => ({ ...x, consent: v }))} error={err.consent} id="complaint-form" />
      <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60 sm:w-auto" data-testid="complaint-form-submit-button">{busy ? c.sending : s.submit} <ArrowRight size={16} /></button>
      <p className="text-xs text-slate-500">{s.alt} <a href="mailto:team@sidly.space" className="underline decoration-teal-500 underline-offset-2" data-testid="complaint-email-link">team@sidly.space</a>.</p>
    </form>
  );
}
